/**
 * Password Validation Utilities
 * Implements strong password security requirements
 */

export interface PasswordValidationResult {
  isValid: boolean
  errors: string[]
  strength: 'weak' | 'fair' | 'good' | 'strong'
}

const PASSWORD_MIN_LENGTH = 12
const PASSWORD_MAX_LENGTH = 128

/**
 * Validates password against security requirements
 * Requirements:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = []

  // Length check
  if (password.length < PASSWORD_MIN_LENGTH) {
    errors.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters long`)
  }

  if (password.length > PASSWORD_MAX_LENGTH) {
    errors.push(`Password must not exceed ${PASSWORD_MAX_LENGTH} characters`)
  }

  // Complexity checks
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter')
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter')
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number')
  }

  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Password must contain at least one special character')
  }

  // Common password patterns check (basic)
  const commonPatterns = [
    /^123456/,
    /password/i,
    /qwerty/i,
    /admin/i,
    /letmein/i,
    /welcome/i,
  ]

  for (const pattern of commonPatterns) {
    if (pattern.test(password)) {
      errors.push('Password contains a common pattern and is too weak')
      break
    }
  }

  // Calculate strength
  let strength: 'weak' | 'fair' | 'good' | 'strong' = 'weak'

  if (errors.length === 0) {
    if (password.length >= 16 && hasVariedCharacters(password)) {
      strength = 'strong'
    } else if (password.length >= 14) {
      strength = 'good'
    } else {
      strength = 'fair'
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    strength,
  }
}

/**
 * Checks if password has good character variety
 */
function hasVariedCharacters(password: string): boolean {
  const uppercaseCount = (password.match(/[A-Z]/g) || []).length
  const lowercaseCount = (password.match(/[a-z]/g) || []).length
  const digitCount = (password.match(/[0-9]/g) || []).length
  const specialCount = (password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || []).length

  return uppercaseCount >= 2 && lowercaseCount >= 2 && digitCount >= 2 && specialCount >= 2
}

/**
 * Gets password strength color for UI
 */
export function getPasswordStrengthColor(strength: 'weak' | 'fair' | 'good' | 'strong'): string {
  switch (strength) {
    case 'weak':
      return 'text-destructive'
    case 'fair':
      return 'text-orange-500'
    case 'good':
      return 'text-yellow-500'
    case 'strong':
      return 'text-green-500'
  }
}

/**
 * Gets password requirements description
 */
export function getPasswordRequirements(): string[] {
  return [
    `At least ${PASSWORD_MIN_LENGTH} characters`,
    'One uppercase letter (A-Z)',
    'One lowercase letter (a-z)',
    'One number (0-9)',
    'One special character (!@#$%...)',
  ]
}
